/**
 * 편집 중에 닫을 때 묻기 — 플레이리스트·캠페인·스케줄·템플릿·콘텐츠 편집 창이 같이 쓴다.
 *
 * 편집 창은 바깥 누르기·Esc·✕·[취소] 어느 것으로도 닫히는데, 예전에는 묻지 않고 닫혀 스무 줄을 고른
 * 편성이 손 한 번에 사라졌다. 고친 것이 있을 때만 묻는다 — 늘 물으면 그냥 보려고 연 사람도 매번 답해야
 * 하고, 곧 읽지 않고 [확인] 을 누르게 된다. 브라우저 탭을 닫거나 새로 고칠 때도 같은 이유로 막는다.
 */
import { useCallback, useEffect, useRef } from 'react';
import { confirmDialog } from './ConfirmDialog.js';

/**
 * 처음 값과 지금 값이 다른가. 처음 값은 `ready` 가 참이 된 첫 그림에서 잡는다 — 서버에서 읽어 오는
 * 편집기(플레이리스트)는 읽기 전의 빈 값을 처음 값으로 삼으면 열자마자 "고침" 이 된다.
 */
export function useDirty(snapshot: unknown, ready = true): boolean {
  const serialized = JSON.stringify(snapshot);
  const baseline = useRef<string | null>(null);
  if (ready && baseline.current === null) baseline.current = serialized;
  return baseline.current !== null && baseline.current !== serialized;
}

/** 묻는 말 — 닫는 길이 여럿이어도 같은 말이어야 한다. */
export const DISCARD_PROMPT = {
  title: '저장하지 않고 닫을까요?',
  body: '고친 내용이 저장되지 않았습니다. 닫으면 사라집니다.',
  confirmLabel: '저장하지 않고 닫기',
  cancelLabel: '계속 편집',
  danger: true,
} as const;

/** 고친 것이 있으면 묻고 닫는 함수. 창의 `onClose` 와 [취소] 단추에 같이 준다. */
export function useGuardedClose(dirty: boolean, onClose: () => void): () => void {
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // 옛 브라우저는 이 값이 있어야 묻는다(문구는 브라우저가 정한다).
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return useCallback(() => {
    if (!dirty) {
      onClose();
      return;
    }
    void confirmDialog(DISCARD_PROMPT).then((ok) => {
      if (ok) onClose();
    });
  }, [dirty, onClose]);
}
